import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  MessageSquare, ChevronUp, ChevronDown, CheckCircle2, Tag, EyeOff,
  Plus, Search, AlertCircle, ArrowLeft, Crown, Sparkles, Zap, Shield,
} from "lucide-react";

const planIcon = (p) => {
  if (p === "founder") return Crown;
  if (p === "premium") return Sparkles;
  if (p === "pro") return Zap;
  return Shield;
};
const planColor = (p) => {
  if (p === "founder") return "text-amber-400";
  if (p === "premium") return "text-violet-400";
  if (p === "pro") return "text-emerald-400";
  return "text-muted-foreground";
};

export function QAList() {
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (activeTag) params.tag = activeTag;
    if (search) params.q = search;
    if (sort) params.sort = sort;
    const { data } = await api.get("/qa/questions", { params });
    setItems(data.items);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/qa/tags").then((r) => setTags(r.data.tags)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [activeTag, sort]);

  return (
    <div className="container-app py-12 max-w-5xl" data-testid="qa-list-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow">Foro entre colegas</div>
          <h1 className="serif text-4xl mt-3">Consultas profesionales</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Resuelve dudas con otros abogados verificados. Bloqueo automático de teléfonos, correos y enlaces para proteger tu cartera.
          </p>
        </div>
        <Link to="/app/foro/nueva" className="btn-primary" data-testid="cta-new-question">
          <Plus size={16} className="mr-1.5" /> Nueva pregunta
        </Link>
      </div>

      <div className="mt-8 grid lg:grid-cols-4 gap-6">
        {/* Sidebar filters */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-2">
              <Search size={14} className="text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Buscar..."
                className="bg-transparent text-sm flex-1 outline-none"
                data-testid="qa-search"
              />
            </div>
          </div>
          <div className="panel p-4">
            <div className="label-eyebrow mb-3">Orden</div>
            <div className="flex flex-col gap-1">
              {[
                { v: "recent", l: "Más recientes" },
                { v: "votes", l: "Más votadas" },
                { v: "unanswered", l: "Sin responder" },
              ].map((s) => (
                <button
                  key={s.v}
                  onClick={() => setSort(s.v)}
                  className={`text-left text-sm px-2 py-1.5 rounded ${sort === s.v ? "bg-white/5 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`sort-${s.v}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
          <div className="panel p-4">
            <div className="label-eyebrow mb-3 flex items-center gap-1"><Tag size={11} /> Áreas</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setActiveTag("")} className={`text-[10px] px-2 py-1 rounded-full border ${!activeTag ? "border-[hsl(var(--primary))] text-foreground" : "border-white/10 text-muted-foreground"}`}>Todas</button>
              {tags.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTag(activeTag === t ? "" : t)}
                  className={`text-[10px] px-2 py-1 rounded-full border ${activeTag === t ? "border-[hsl(var(--primary))] text-foreground bg-white/5" : "border-white/10 text-muted-foreground hover:text-foreground"}`}
                  data-testid={`tag-filter-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Question list */}
        <div className="lg:col-span-3 space-y-3">
          {loading ? (
            <p className="text-muted-foreground text-sm">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="panel p-10 text-center text-muted-foreground">
              <MessageSquare className="mx-auto mb-3" size={20} />
              No hay preguntas aún. ¡Sé el primero en consultar!
            </div>
          ) : (
            items.map((q) => <QuestionRow key={q.id} q={q} />)
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionRow({ q }) {
  const PlanIcon = planIcon(q.author_plan);
  return (
    <Link to={`/app/foro/${q.id}`} className="block panel p-5 hover:ring-1 hover:ring-[hsl(var(--primary))]/30 transition-all" data-testid={`question-row-${q.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center text-xs text-muted-foreground shrink-0 w-12">
          <div className="serif text-xl text-foreground">{q.score}</div>
          <div>votos</div>
          <div className={`serif text-xl mt-2 ${q.answers_count > 0 ? (q.accepted_answer_id ? "text-emerald-400" : "text-foreground") : "text-muted-foreground"}`}>{q.answers_count}</div>
          <div>resp.</div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium leading-snug">{q.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {q.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full border border-white/10 text-muted-foreground">{t}</span>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
            {q.anonymous ? (
              <span className="inline-flex items-center gap-1"><EyeOff size={11} /> Anónimo</span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <PlanIcon size={11} className={planColor(q.author_plan)} />
                {q.author_name}
              </span>
            )}
            <span>·</span>
            <span>{new Date(q.created_at).toLocaleDateString("es-CL")}</span>
            <span>·</span>
            <span>{q.views} vistas</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function QANew() {
  const nav = useNavigate();
  const [tags, setTags] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", tags: [], anonymous: false });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/qa/tags").then((r) => setTags(r.data.tags));
  }, []);

  const toggleTag = (t) => {
    setForm((f) => f.tags.includes(t) ? { ...f, tags: f.tags.filter(x => x !== t) } : (f.tags.length >= 5 ? f : { ...f, tags: [...f.tags, t] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const { data } = await api.post("/qa/questions", form);
      nav(`/app/foro/${data.question.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al publicar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-app py-12 max-w-3xl" data-testid="qa-new-page">
      <Link to="/app/foro" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14} /> Volver al foro
      </Link>
      <h1 className="serif text-3xl">Nueva consulta</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Está prohibido incluir teléfonos, correos, enlaces o menciones a WhatsApp/Instagram/Telegram. La plataforma bloqueará el envío automáticamente.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <label className="label-eyebrow mb-2 block">Título</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="¿Cuál es tu pregunta? Sé específico."
            className="w-full px-4 py-3 rounded-md border border-white/10 bg-white/5 outline-none"
            data-testid="qa-title-input"
            required
            minLength={10}
            maxLength={200}
          />
        </div>
        <div>
          <label className="label-eyebrow mb-2 block">Detalle</label>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Contexto del caso, jurisprudencia aplicable, intentos previos..."
            className="w-full px-4 py-3 rounded-md border border-white/10 bg-white/5 outline-none min-h-[160px]"
            data-testid="qa-body-input"
            required
            minLength={20}
            maxLength={5000}
          />
        </div>
        <div>
          <label className="label-eyebrow mb-2 block">Áreas (máx 5)</label>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTag(t)}
                className={`text-xs px-2.5 py-1 rounded-full border ${form.tags.includes(t) ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground"}`}
                data-testid={`qa-tag-${t}`}
              >{t}</button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="qa-anonymous-toggle">
          <input
            type="checkbox"
            checked={form.anonymous}
            onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
            className="accent-[hsl(var(--primary))]"
          />
          <EyeOff size={14} /> Publicar de forma anónima
        </label>

        {error && (
          <div className="panel p-3 bg-red-500/10 border border-red-500/30 text-sm text-red-300 flex items-start gap-2" data-testid="qa-error">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="qa-submit">
          {submitting ? "Publicando…" : "Publicar consulta"}
        </button>
      </form>
    </div>
  );
}

export function QADetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answerBody, setAnswerBody] = useState("");
  const [answerAnon, setAnswerAnon] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/qa/questions/${id}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo cargar");
    }
  };
  useEffect(() => { load(); }, [id]);

  const vote = async (target, targetId, direction) => {
    try {
      await api.post(`/qa/${target}s/${targetId}/vote`, { direction });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const accept = async (aid) => {
    try {
      await api.post(`/qa/answers/${aid}/accept`);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const postAnswer = async (e) => {
    e.preventDefault();
    setPostError(null); setPosting(true);
    try {
      await api.post(`/qa/questions/${id}/answers`, { body: answerBody, anonymous: answerAnon });
      setAnswerBody(""); setAnswerAnon(false);
      await load();
    } catch (e) {
      setPostError(e.response?.data?.detail || "Error al publicar");
    } finally {
      setPosting(false);
    }
  };

  if (error) return <div className="container-app py-16"><p className="text-red-400">{error}</p></div>;
  if (!data) return <div className="container-app py-16"><p className="text-muted-foreground">Cargando…</p></div>;

  const q = data.question;
  const PlanIcon = planIcon(q.author_plan);
  const isOwner = q.is_own;

  return (
    <div className="container-app py-12 max-w-4xl" data-testid="qa-detail-page">
      <Link to="/app/foro" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14} /> Foro
      </Link>

      <article className="panel p-6" data-testid="question-block">
        <div className="flex items-start gap-4">
          <VoteWidget item={q} onVote={(d) => vote("question", q.id, d)} canVote={!q.is_own} />
          <div className="flex-1 min-w-0">
            <h1 className="serif text-2xl leading-snug">{q.title}</h1>
            <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{q.body}</div>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              {q.tags.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-full border border-white/10 text-muted-foreground">{t}</span>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              {q.anonymous ? (
                <span className="inline-flex items-center gap-1"><EyeOff size={11} /> Anónimo</span>
              ) : (
                <span className="inline-flex items-center gap-1"><PlanIcon size={11} className={planColor(q.author_plan)} />{q.author_name}</span>
              )}
              · {new Date(q.created_at).toLocaleDateString("es-CL")} · {q.views} vistas
            </div>
          </div>
        </div>
      </article>

      <h2 className="serif text-xl mt-10 mb-3" data-testid="answers-heading">{data.answers.length} {data.answers.length === 1 ? "respuesta" : "respuestas"}</h2>
      <div className="space-y-3">
        {data.answers.map((a) => {
          const PI = planIcon(a.author_plan);
          return (
            <article key={a.id} className={`panel p-5 ${a.is_accepted ? "ring-2 ring-emerald-400/40" : ""}`} data-testid={`answer-${a.id}`}>
              <div className="flex items-start gap-4">
                <VoteWidget item={a} onVote={(d) => vote("answer", a.id, d)} canVote={!a.is_own} />
                <div className="flex-1 min-w-0">
                  {a.is_accepted && (
                    <div className="mb-2 inline-flex items-center gap-1.5 text-xs text-emerald-400 font-medium">
                      <CheckCircle2 size={14} /> Respuesta aceptada por el autor
                    </div>
                  )}
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{a.body}</div>
                  <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="inline-flex items-center gap-1">
                      {a.anonymous ? <><EyeOff size={11} /> Anónimo</> : <><PI size={11} className={planColor(a.author_plan)} />{a.author_name}</>}
                      · {new Date(a.created_at).toLocaleDateString("es-CL")}
                    </div>
                    {isOwner && !a.is_accepted && (
                      <button onClick={() => accept(a.id)} className="text-xs px-3 py-1 rounded border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" data-testid={`accept-${a.id}`}>
                        Marcar como aceptada
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <h3 className="serif text-lg mt-10 mb-3">Tu respuesta</h3>
      <form onSubmit={postAnswer} className="panel p-5 space-y-4" data-testid="answer-form">
        <textarea
          value={answerBody}
          onChange={(e) => setAnswerBody(e.target.value)}
          placeholder="Comparte tu experiencia o referencias. Sin teléfonos, correos ni enlaces."
          className="w-full px-3 py-2 rounded-md border border-white/10 bg-white/5 outline-none min-h-[120px]"
          required
          minLength={10}
          maxLength={5000}
          data-testid="answer-body"
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={answerAnon} onChange={(e) => setAnswerAnon(e.target.checked)} className="accent-[hsl(var(--primary))]" />
            <EyeOff size={12} /> Anónimo
          </label>
          <button type="submit" disabled={posting} className="btn-primary text-sm" data-testid="answer-submit">
            {posting ? "Publicando…" : "Publicar respuesta"}
          </button>
        </div>
        {postError && (
          <div className="text-xs text-red-300 flex items-start gap-2 bg-red-500/10 p-3 rounded">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />{postError}
          </div>
        )}
      </form>
    </div>
  );
}

function VoteWidget({ item, onVote, canVote }) {
  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0 w-10">
      <button
        onClick={() => canVote && onVote("up")}
        disabled={!canVote}
        className={`p-1 rounded ${item.user_vote === "up" ? "text-[hsl(var(--accent))]" : "text-muted-foreground hover:text-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid={`vote-up-${item.id}`}
        title={canVote ? "Votar a favor" : "No puedes votar tu propia publicación"}
      >
        <ChevronUp size={20} />
      </button>
      <div className="serif text-lg leading-none">{item.score}</div>
      <button
        onClick={() => canVote && onVote("down")}
        disabled={!canVote}
        className={`p-1 rounded ${item.user_vote === "down" ? "text-red-400" : "text-muted-foreground hover:text-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid={`vote-down-${item.id}`}
      >
        <ChevronDown size={20} />
      </button>
    </div>
  );
}
